import { useMemo } from 'react'

import { HAUTEUR_CRENEAU_REM } from '../../lib/config'
import type { InfirmierPlanning, InterventionPlanning, SemainePlanning } from '../../lib/api/types'
import { formatDate } from '../../lib/formatters'
import {
  creneaux,
  JOURS_SEMAINE_COURT,
  minutesDepuisMinuit,
  positionDansGrille,
} from '../../lib/temps'
import { CarteIntervention } from './CarteIntervention'

interface Proprietes {
  semaine: SemainePlanning
  /** Déjà filtrées par la page : la grille ne filtre rien elle-même. */
  interventionsParJour: InterventionPlanning[][]
  interventionSelectionnee: string | null
  interactive: boolean
  onOuvrir: (intervention: InterventionPlanning) => void
  onDeplacer: (intervention: InterventionPlanning, date: string, heure: string) => void
}

/**
 * La grille hebdomadaire.
 *
 * ============================================================================
 * **Une seule CSS Grid pour toute la semaine**, et non un `<table>` avec des
 * `rowspan`. Le `rowspan` casse dès que deux interventions se chevauchent dans
 * la même journée — ce qui arrive tous les matins à 08:00 — et le
 * positionnement absolu dans une cellule de tableau n'est pas fiable d'un
 * navigateur à l'autre.
 *
 * Les chevauchements se résolvent dans la colonne du jour : les interventions
 * qui se recouvrent se partagent la largeur, chacune décalée de sa part.
 * ============================================================================
 */
export function GrilleSemaine({
  semaine,
  interventionsParJour,
  interventionSelectionnee,
  interactive,
  onOuvrir,
  onDeplacer,
}: Proprietes) {
  const { heure_ouverture, heure_fermeture, pas_minutes } = semaine.amplitude

  const heures = useMemo(
    () => creneaux(heure_ouverture, heure_fermeture, pas_minutes),
    [heure_ouverture, heure_fermeture, pas_minutes],
  )

  const infirmiersParId = useMemo(() => {
    const carte = new Map<string, InfirmierPlanning>()
    semaine.infirmiers.forEach((i) => carte.set(i.id, i))
    return carte
  }, [semaine.infirmiers])

  /**
   * Groupes de chevauchement, calculés une fois par jour.
   *
   * Le vrai coût d'un planning n'est pas le DOM — 300 blocs restent
   * confortables — mais un `filter` recalculé pour chaque bloc. Ce `useMemo`
   * est ce qui fait la différence entre 40 ms et 400 ms.
   */
  const dispositionParJour = useMemo(
    () =>
      interventionsParJour.map((interventions) => {
        const triees = [...interventions].sort((a, b) =>
          a.heure_debut.localeCompare(b.heure_debut),
        )
        const placement = new Map<string, { index: number; total: number }>()

        let groupe: InterventionPlanning[] = []
        let finGroupe = -1

        const cloturer = () => {
          groupe.forEach((x, i) => placement.set(x.id, { index: i, total: groupe.length }))
          groupe = []
        }

        triees.forEach((intervention) => {
          const debut = minutesDepuisMinuit(intervention.heure_debut)
          if (groupe.length > 0 && debut >= finGroupe) cloturer()
          groupe.push(intervention)
          finGroupe = Math.max(finGroupe, debut + intervention.duree_minutes)
        })
        cloturer()

        return { triees, placement }
      }),
    [interventionsParJour],
  )

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <div
        className="grid min-w-[64rem]"
        style={{
          gridTemplateColumns: '4rem repeat(7, minmax(9rem, 1fr))',
          gridTemplateRows: `auto repeat(${heures.length}, ${HAUTEUR_CRENEAU_REM}rem)`,
        }}
        role="grid"
        aria-label={`Planning de la semaine du ${formatDate(semaine.lundi)} au ${formatDate(semaine.dimanche)}`}
      >
        {/* En-tête : coin vide + sept jours, collant au défilement. */}
        <div className="sticky top-0 z-20 border-b border-r border-slate-200 bg-slate-50" />
        {semaine.jours.map((jour, index) => (
          <div
            key={jour.date}
            className="sticky top-0 z-20 border-b border-r border-slate-200 bg-slate-50 px-2 py-1.5 text-center"
            role="columnheader"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {JOURS_SEMAINE_COURT[index]}
            </p>
            <p className="text-sm font-medium text-slate-800">{formatDate(jour.date).slice(0, 5)}</p>
            <p className="text-[10px] text-slate-500">
              {jour.interventions.length} interv.
              {jour.non_affectees.length > 0 ? ` · ${jour.non_affectees.length} à placer` : ''}
            </p>
          </div>
        ))}

        {/* Colonne des heures + fond des cellules. */}
        {heures.map((heure, ligne) => (
          <div
            key={`heure-${heure}`}
            className="sticky left-0 z-10 border-b border-r border-slate-200 bg-white pr-1 text-right text-[10px] tabular-nums text-slate-400"
            style={{ gridColumn: 1, gridRow: ligne + 2 }}
          >
            {heure.endsWith(':00') ? heure : ''}
          </div>
        ))}

        {semaine.jours.map((jour, indexJour) =>
          heures.map((heure, ligne) => (
            <div
              key={`case-${jour.date}-${heure}`}
              className={`border-b border-r border-slate-100 ${
                heure.endsWith(':00') ? 'border-b-slate-200' : ''
              } ${indexJour >= 5 ? 'bg-slate-50/60' : ''}`}
              style={{ gridColumn: indexJour + 2, gridRow: ligne + 2 }}
              onDragOver={interactive ? (e) => e.preventDefault() : undefined}
              onDrop={
                interactive
                  ? (e) => {
                      e.preventDefault()
                      const id = e.dataTransfer.getData('text/plain')
                      const source = interventionsParJour
                        .flat()
                        .find((x) => x.id === id)
                      if (source) onDeplacer(source, jour.date, heure)
                    }
                  : undefined
              }
            />
          )),
        )}

        {/* Les interventions, par-dessus le fond. */}
        {semaine.jours.map((_jour, indexJour) => {
          const disposition = dispositionParJour[indexJour]
          if (!disposition) return null
          return disposition.triees.map((intervention) => {
            const position = positionDansGrille(
              intervention.heure_debut,
              intervention.duree_minutes,
              heure_ouverture,
              heure_fermeture,
              pas_minutes,
            )
            const place = disposition.placement.get(intervention.id) ?? { index: 0, total: 1 }
            return (
              <CarteIntervention
                key={intervention.id}
                intervention={intervention}
                infirmier={
                  intervention.utilisateur_id
                    ? infirmiersParId.get(intervention.utilisateur_id)
                    : undefined
                }
                ligneDebut={position.ligneDebut}
                nbLignes={position.nbLignes}
                colonne={indexJour + 2}
                deborde={position.deborde}
                indexChevauchement={place.index}
                nbChevauchements={place.total}
                selectionnee={interventionSelectionnee === intervention.id}
                interactive={interactive}
                onOuvrir={onOuvrir}
              />
            )
          })
        })}
      </div>
    </div>
  )
}
